import * as React from 'react';
import { Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import * as url from 'url';
import * as ApiClient from './api';
import { VerifyActivationTokenApiData, VerifyActivationTokenApiSuccessResponse } from './api/model';
import * as ApiUtils from './api/utils';
import AppInsightsService from './app-insights/service';
import { SeverityLevel } from './app-insights/severity-level';
import './../node_modules/bootstrap/dist/css/bootstrap.min.css';
import {
  AsyncActivatePage,
  AsyncAlertsPage,
  AsyncContactUsPage,
  // AsyncHomePage,
  // AsyncHomePageV2,
  AsyncHomePageV3,
  AsyncIdentityPage,
  AsyncPrivacyPolicyPage,
  AsyncProtectionPage,
  AsyncResourceCentrePage,
  AsyncScamDetectionPage,
  AsyncTermsConditionsPage,
  AsyncTermsOfUsePage
} from './components/loadable';
import { RouterNavLink } from './components/router-nav-link';
import SiteFooter from './components/site-footer';
import SiteHeader from './components/site-header';
import SiteNavBar from './components/site-navbar';
import WaitSpinnerCard from './components/wait-spinner-card';
import {
  HOST,
  LANGUAGE_CODE_EN_CA,
  LANGUAGE_CODE_FR_CA,
  ROUTE_PATH_ACTIVATE,
  // ROUTE_PATH_ACTIVATE_MAINTENANCE,
  ROUTE_PATH_ALERTS,
  ROUTE_PATH_CONTACT,
  ROUTE_PATH_FAQ,
  ROUTE_PATH_HOME,
  ROUTE_PATH_IDENTITY,
  ROUTE_PATH_PRIVACY_POLICY,
  ROUTE_PATH_PROTECTION,
  ROUTE_PATH_RESOURCE_CENTRE,
  ROUTE_PATH_SCAM,
  ROUTE_PATH_TERMS_CONDITIONS,
  ROUTE_PATH_TERMS_OF_USE,
  SHOW_MAINTENANCE_PAGE
} from './constants';
import FaqPage from './FaqPage';
import { HttpError } from './globals';
import { GoogleAnalyticsService } from './GoogleAnalytics.service';
import { default as i18n, getDocumentTitle } from './locales';
import { MixpanelService } from './mixpanel.service';
import './scss/app.scss';
import './scss/react-accessible-accordion.scss';
import * as SessionStore from './session-store.service';
import * as AppUtils from './utils';


type AppProps = RouteComponentProps<{}>;


interface AppState {
  accessDenied: boolean;
  tokenVerified: boolean;
}

// ****************************************************************************
// route path to translation key mapping (arranged in display order)
//
const SITE_MAP: { [key: string]: string } = {
  [ROUTE_PATH_HOME]: 'global:siteNavbar.menu.home.children',
  [ROUTE_PATH_SCAM]: 'global:siteNavbar.menu.scamdetection.children',
  [ROUTE_PATH_PROTECTION]: 'global:siteNavbar.menu.lostWalletProtection.children',
  [ROUTE_PATH_RESOURCE_CENTRE]: 'protectionPage:featureList.1.action.resourceCentre.children',
  [ROUTE_PATH_ALERTS]: 'global:siteNavbar.menu.monitoringAndAlerts.children',
  [ROUTE_PATH_IDENTITY]: 'global:siteNavbar.menu.identityRestoration.children',
  [ROUTE_PATH_TERMS_OF_USE]: 'global:siteFooter.navMenu.termsOfUse.children',
  [ROUTE_PATH_PRIVACY_POLICY]: 'global:siteFooter.navMenu.privacyPolicy.children',
  [ROUTE_PATH_FAQ]: 'global:siteFooter.navMenu.faq.children',
  [ROUTE_PATH_TERMS_CONDITIONS]: 'global:siteFooter.navMenu.termsAndConditions.children',
  [ROUTE_PATH_CONTACT]: 'global:siteHeader.navMenu.contact.children'
};

/**
 * Represents the entry point of the application. Responsible for:
 *   1. Rendering the application shell (i.e. header, navbar, footer)
 *   2. Base routing setup
 *   3. Verification/Reverification of the activation token, if any, received in the URL
 *   4. Mixpanel initialization and setup of automatic page-view tracking
 *   5. Resetting document title (i.e. browser window title) on route navigation
 *   6. Resetting scroll position to top of the page on route navigation
 *   7. Providing a workaround for react-router's activeClassName defect
 */
class App extends React.Component<AppProps, AppState> {
  public state = {
    accessDenied: false,
    tokenVerified: false
  };

  private readonly _initialPageLoadStart = AppUtils.getTimestamp();

  constructor(props: AppProps) {
    super(props);
    this.renderAccessDenied = this.renderAccessDenied.bind(this);
    this.render404NotFound = this.render404NotFound.bind(this);
    this.onLanguageToggleClick = this.onLanguageToggleClick.bind(this);
  }

  public componentDidMount(): void {
    this.resetDocumentTitle(Object.assign({}, this.props, { location: { pathname: '' } }));
    this.resetActiveNavLink();

    MixpanelService.initialize(process.env.REACT_APP_MP_TOKEN || '').then(() => {
      this.trackPageView(AppUtils.getTimestamp() - this._initialPageLoadStart);
    });

    
    this.fetchLanguageToggleForm();
  }

  // tslint:disable-next-line:no-any
  public getSnapshotBeforeUpdate(prevProps: AppProps): { [key: string]: any } | null {
    const { location: newLocation } = this.props;
    const { location: prevLocation } = prevProps;
    if (newLocation !== prevLocation && !newLocation.hash) {
      return { pageLoadStart: AppUtils.getTimestamp() };
    }
    return null;
  }

  // tslint:disable-next-line:no-any
  public componentDidUpdate(prevProps: AppProps, unused: AppState, snapshot?: { [key: string]: any }): void {
    this.resetDocumentTitle(prevProps);
    this.resetActiveNavLink();

    const { location: newLocation } = this.props;
    const { location: prevLocation } = prevProps;
    if (newLocation !== prevLocation && !newLocation.hash) {
      const duration = snapshot ? AppUtils.getTimestamp() - snapshot.pageLoadStart : undefined;
      this.trackPageView(duration);
     

      if (newLocation.pathname === ROUTE_PATH_HOME) {
        // re-verify token's activation state after coming back
        // from something other than the home page
        this.setState({ tokenVerified: false });
      }
    }
  }

  public render(): React.ReactNode {    
    const ShowMaintenancePage = SHOW_MAINTENANCE_PAGE();
    if (this.state.accessDenied) {
      return <Route render={this.renderAccessDenied} />;
    }
    
    return (
      <React.Fragment>
        {this.state.tokenVerified ? (
          <React.Fragment>
          <SiteHeader onClick={{ languageToggle: this.onLanguageToggleClick }} />
            {!ShowMaintenancePage && <SiteNavBar />}
            <main id="mainContent" tabIndex={-1}>
              <Switch>
                {/* <Route exact={true} path={ROUTE_PATH_HOME} component={AsyncHomePage} /> */}
                {/* <Route exact={true} path={ROUTE_PATH_HOME} component={AsyncHomePageV2} /> */}
                <Route exact={true} path={ROUTE_PATH_HOME} component={AsyncHomePageV3} />
                <Route exact={true} path={ROUTE_PATH_ALERTS} component={AsyncAlertsPage} />
                <Route exact={true} path={ROUTE_PATH_IDENTITY} component={AsyncIdentityPage} />
                 <Route exact={true} path={ROUTE_PATH_SCAM} component={AsyncScamDetectionPage} />
                <Route exact={true} path={ROUTE_PATH_PROTECTION} component={AsyncProtectionPage} />
                <Route exact={true} path={ROUTE_PATH_RESOURCE_CENTRE} component={AsyncResourceCentrePage} />

                <Route exact={false} path={ROUTE_PATH_ACTIVATE} component={AsyncActivatePage} />

                <Route
                  exact={true}
                  path={ROUTE_PATH_TERMS_OF_USE}
                  component={() => <AsyncTermsOfUsePage className="page-terms-of-use" />}
                />

                <Route
                  exact={true}
                  path={ROUTE_PATH_PRIVACY_POLICY}
                  component={() => <AsyncPrivacyPolicyPage className="page-privacy-policy" />}
                />

                {/* <Route exact={true} path={ROUTE_PATH_FAQ} component={() => <AsyncFaqPage className="page-faq" />} /> */}
                <Route exact={true} path={ROUTE_PATH_FAQ} component={FaqPage} />

                <Route
                  exact={true}
                  path={ROUTE_PATH_TERMS_CONDITIONS}
                  component={() => <AsyncTermsConditionsPage className="page-conditions" />}
                />

                <Route exact={true} path={ROUTE_PATH_CONTACT} component={AsyncContactUsPage} />

                <Route render={this.render404NotFound} />
              </Switch>
            </main>
            <SiteFooter />
          </React.Fragment>
        ) : (

          //{debugger;}
          this.renderWaitSpinnerAndVerifyToken()
        )}
      </React.Fragment>
    );
  }

  public renderAccessDenied(): React.ReactNode {
    return (
      <React.Fragment>
        <header className="site-header">
          <div className="row">
            <div className="col-logo-image">{this.renderLogo()}</div>
          </div>
        </header>
        <article className="page-access-denied">
          <h1 className="page-title" dangerouslySetInnerHTML={{ __html: i18n.t('pageAccessDenied:pageTitle') }} />
          <div className="page-content" dangerouslySetInnerHTML={{ __html: i18n.t('pageAccessDenied:pageContent') }} />
          <div className="page-background" />
        </article>
      </React.Fragment>
    );
  }

  public render404NotFound(): React.ReactNode {
    const siteMapListItems = Object.keys(SITE_MAP).map((pathname) => (
      <li key={pathname}>
        <RouterNavLink to={pathname}>{i18n.t(SITE_MAP[pathname])}</RouterNavLink>
      </li>
    ));
    return (
      <article className="page-not-found">
        <div className="row-site-map">
          <h1 className="page-title" dangerouslySetInnerHTML={{ __html: i18n.t('pageNotFound:pageTitle') }} />
          <p className="site-map-title" dangerouslySetInnerHTML={{ __html: i18n.t('pageNotFound:siteMapTitle') }} />
          <ul className="list-site-map">{siteMapListItems}</ul>
        </div>
      </article>
    );
  }

  public onLanguageToggleClick(event: React.MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const elLanguageToggleForm = document.querySelector<HTMLFormElement>('#languageToggleForm');
    if (elLanguageToggleForm) {
      const elInputUniqueID = elLanguageToggleForm.querySelector<HTMLInputElement>('#uid');
      const elInputClientID = elLanguageToggleForm.querySelector<HTMLInputElement>('#cc');
      if (elInputUniqueID && elInputClientID) {
        const altLanguageCode = i18n.language === LANGUAGE_CODE_EN_CA ? LANGUAGE_CODE_FR_CA : LANGUAGE_CODE_EN_CA;
        // const altLanguageUrl = url.parse(String(window.location), false);
        const altLanguageUrl = url.parse(ROUTE_PATH_HOME, false);
        altLanguageUrl.host = (HOST.find((host) => host.value === altLanguageCode) || { key: window.location.host }).key;
        elLanguageToggleForm.action = url.format(altLanguageUrl);

        elInputUniqueID.value = SessionStore.getActivationToken();
        elInputClientID.value = process.env.REACT_APP_CLIENT_CODE || '';

        elLanguageToggleForm.submit();
      }
    }
  }

  private renderLogo(): React.ReactNode {
    const { alt, ...logoImageProps } = i18n.t('siteHeader.logoImage') as React.ImgHTMLAttributes<HTMLImageElement>;
    logoImageProps.className = [logoImageProps.className, 'img-logo-header'].join(' ');
    logoImageProps.tabIndex = 0;
    const trimmedAlt = (alt || '').trim();
    if (trimmedAlt.length === 0) {
      logoImageProps.role = 'presentation';
    }
    return <img alt={trimmedAlt} {...logoImageProps} />;
  }

  private fetchLanguageToggleForm(): void {
    const altLanguageCode = i18n.language === LANGUAGE_CODE_EN_CA ? LANGUAGE_CODE_FR_CA : LANGUAGE_CODE_EN_CA;
    fetch(`/form/?lng=${altLanguageCode}`, { cache: 'no-store' })
      .then((response) => {
        if (response.status !== 200) {
          throw new HttpError(response);
        }
        return response.text();
      })
      .then((languageToggleFormHtml) => {
        const trimmedHtml = typeof languageToggleFormHtml === 'string' && languageToggleFormHtml.trim();
        if (trimmedHtml && trimmedHtml.startsWith('<form')) {
          const elLanguageToggleForm = document.createElement('div');
          elLanguageToggleForm.innerHTML = trimmedHtml;
          document.body.appendChild(elLanguageToggleForm);
        }
      })
      .catch((error) => {
        if (process.env.REACT_APP_ENV !== 'production') {
          let errorMessage = error instanceof Error ? error.message : 'Error fetching language toggle form.';
          if (error instanceof Response) {
            errorMessage += ` (HTTP ${error.status}: ${error.statusText})`;
          }
          console.log(`ERROR: App::fetchLanguageToggleForm: ${errorMessage}`);
        }
      });
  }

  // **************************************************************************
  // This method renders a wait spinner component while it calls an API to
  // verify the activation state of the given token. It is also responsible
  // for setting the necessary flags in browser's session storage indicating
  // whether the current user is already a member or not.
  //
  private renderWaitSpinnerAndVerifyToken(): React.ReactNode {
    AppInsightsService.trackTrace({ message: 'Begin verify token', severityLevel: SeverityLevel.Information });
    const activationToken = SessionStore.getActivationToken();

    const body: VerifyActivationTokenApiData = { activationToken };
    const promise =
      activationToken.length === 0
        ? Promise.resolve<VerifyActivationTokenApiSuccessResponse>({ activated: true })
        : ApiClient.verifyActivationToken({ body });

    promise
      .then((response) => {
        AppInsightsService.trackTrace({ message: 'End verify token', severityLevel: SeverityLevel.Information });

        SessionStore.setActivationTokenVerified(true);
        SessionStore.setActivationTokenActivated(response.activated);
        this.setState({ tokenVerified: true });
      })
      .catch(async (error) => {
        AppInsightsService.trackException({ exception: error });

        let errorCode = 0;
        if (AppUtils.isObject(error)) {
          if (error.response instanceof Response) {
            const responseJson = await AppUtils.tryGetResponseJson(error.response);
            if (ApiUtils.isErrorResponse(responseJson) && responseJson.Errors.length > 0) {
              errorCode = responseJson.Errors[0].Code;
            }

            if (process.env.REACT_APP_ENV !== 'production') {
              ApiUtils.extractApiErrorMessageFromResponse(error.response, i18n.t('global:genericErrorMessage')).then((errorMessage) => {
                console.log(`ERROR: App::queueVerifyTokenAndRenderWaitSpinner: ${errorMessage}`);
              });
            }
          } else {
            console.log(`ERROR: App::queueVerifyTokenAndRenderWaitSpinner: ${error}`);
          }
        }

        // is user's IP not within the allowed region codes?
        if (errorCode === 31105) {
          SessionStore.setActivationToken('', true /* removeVerifiedAndActivated */);
          this.setState({ accessDenied: true });
          return;
        }

        // in case we're unable to connect to the verifyToken API, the safest bet
        // is to assume that the token is already activated and show the LOGIN button
        SessionStore.setActivationTokenVerified(true);
        SessionStore.setActivationTokenActivated(true);
        this.setState({ tokenVerified: true });
      });

    const waitMessage: string = i18n.t('global:pleaseWaitText');
    return <WaitSpinnerCard>{waitMessage}</WaitSpinnerCard>;
  }

  // **************************************************************************
  // This method is responsible for updating the browser window title and
  // resetting the scroll position to top of the document on route navigation.
  //
  private resetDocumentTitle(prevProps: AppProps): void {
    const newLocation = this.props.location;
    const prevLocation = prevProps.location;

    // tslint:disable-next-line:no-console
    // console.log({ newLocation, prevLocation });

    if (!newLocation.hash && newLocation !== prevLocation) {
      document.title = getDocumentTitle(newLocation.pathname);

      // reset scroll position to top of the document
      window.scroll(0, 0);

      // reset focus to header logo image
      const headerLogoImageSelector = ['.site-header', ['.col-logo-image', '.link-logo-image', '.img-logo-header'].join('>')].join(' ');
      const headerLogoImage = document.querySelector<HTMLAnchorElement>(headerLogoImageSelector);
      if (headerLogoImage) {
        headerLogoImage.focus();
      }
    }
  }

  // ************************************************************************
  // Workaround for the following react-router defect:
  //
  // Active class is not being added to NavLinks on active pages on v4.3.1
  // https://github.com/ReactTraining/react-router/issues/6201
  //
  private resetActiveNavLink(): void {
    const newLocation = this.props.location;

    Array.prototype.forEach.call(document.getElementsByClassName('nav-link active'), (el: Element) => {
      el.classList.remove('active');
      el.removeAttribute('aria-current');
    });

    const elNextActiveNavLink = document.querySelector(`.nav-link[href="${newLocation.pathname}"]`);
    if (elNextActiveNavLink) {
      elNextActiveNavLink.setAttribute('aria-current', 'page');
      elNextActiveNavLink.classList.add('active');
    }
  }

  // ************************************************************************
  // Automatically sends a page view event on route navigation.
  //
  private trackPageView(duration?: number): void {
    const { location: newLocation } = this.props;
    if (!newLocation.hash && !newLocation.pathname.startsWith(ROUTE_PATH_ACTIVATE)) {
      let docTitle = getDocumentTitle(newLocation.pathname, { lng: LANGUAGE_CODE_EN_CA });
      if (newLocation.pathname === ROUTE_PATH_HOME) {
        docTitle += ` (${SessionStore.getActivationTokenActivated() ? 'Members' : 'Non-members'})`;
      }
      //MixpanelService.trackPageView(docTitle);
      new GoogleAnalyticsService().eventEmitter("PageView", "engagement", newLocation.pathname, 1)
      AppInsightsService.trackPageView({ name: docTitle, url: newLocation.pathname, duration });
    }
  }
  
}

export default withRouter(App);
